import { Type } from '@deqo/foundation';
import { camelCase } from 'change-case';
import { injectable } from 'inversify';
import { Component, ComponentDescriptor } from '../../../concepts/objects';
import { Activator, Connector } from '../../Container';
import { Tag } from '../annotations/Tag';
import { LensAspect } from './LensAspect';
import { LensAspectProvider } from './LensAspectProvider';

class ComponentStaticsLensAspectProvider implements LensAspectProvider {

    private aspects = [
        LensAspect.create(
            'string',
            'Component.id',
            'id',
            'The component ID',
            'property',
            elmt => elmt.id,
        ),
        LensAspect.create(
            'string',
            'Component.type',
            'type',
            'The component type',
            'property',
            elmt => elmt.type,
        ),
    ];

    public getAspectsFor(elmts: Component[]): LensAspect[] {
        return this.aspects;
    }
}

class ComponentPropertyLensAspectProvider implements LensAspectProvider {

    public getAspectsFor(elmts: Component[]): LensAspect[] {

        const descs = new Set<ComponentDescriptor>();
        for (const e of elmts) {
            descs.add(Component.describe(e));
        }

        return Array.from(descs).flatMap(x => this.getAspectsForDescriptor(x));
    }

    private getAspectsForDescriptor(desc: ComponentDescriptor): LensAspect[] {
        return desc.properties.map(x =>
            LensAspect.create(
                x.type.name,
                `${desc.name}.${x.name}`,
                x.name,
                '',
                'property',
                elmt => elmt.has(x.name) ? elmt.get(x.name) : null,
            )
        );
    }
}

class TagPropertyLensAspectProvider implements LensAspectProvider {

    public getAspectsFor(elmts: Component[]): LensAspect[] {

        const tags = new Set(
            elmts.flatMap(x => x.polarize(Tag)).map(x => x.name)
        );

        return Array.from(tags).map(x => this.getAspectForTagName(x));
    }

    private getAspectForTagName(tagName: string): LensAspect {
        return LensAspect.create(
            'string',
            `Tag.${tagName}`,
            this.getSafeTagName(tagName),
            '',
            'assocation',
            elmt => {
                const tag = elmt.children.find(x => x.isAnd(Tag, t => t.name.toLowerCase() === tagName.toLowerCase()))?.as(Tag);
                return tag?.value || tag?.name || null;
            }
        );
    }

    private getSafeTagName(tagName: string): string {
        tagName = camelCase(tagName);
        tagName = tagName.replace(/\s+/g, '');
        if (tagName.match(/^[a-zA-Z0-9]+$/)) return tagName;
        return `_${tagName}`;
    }
}

@injectable()
export class LensReflector {

    private providers = [] as LensAspectProvider[];

    constructor() {
        this.providers.push(new ComponentStaticsLensAspectProvider());
        this.providers.push(new ComponentPropertyLensAspectProvider());
        this.providers.push(new TagPropertyLensAspectProvider());
    }

    public extend(provider: LensAspectProvider) {
        this.providers.push(provider);
    }

    public find(elmts: Component[], name: string) {
        return this.reflect(elmts).find(x => x.name === name);
    }

    public reflect(elmts: Component[]): LensAspect[] {
        return this.providers.flatMap(x => x.getAspectsFor(elmts));
    }

    public createProxies<T extends Component>(elmts: T[]): [T, unknown][] {
        const aspects = this.reflect(elmts);
        const lookup = new Map(aspects.map(x => [x.name.toLowerCase(), x.read]));

        return elmts.map(x => [
            x,
            new Proxy(x, {
                // Intercept all property access
                get: (target, prop) => {
                    if (typeof (prop) === 'symbol') return null;
                    const reader = lookup.get(prop.toLowerCase());
                    if (reader) return reader(target);
                    if (prop in target) return target[prop];
                    return null;
                }
            })
        ]);
    }
}

export const inLensAspectProvider = (providerType: Type<LensAspectProvider>): Connector =>
    r => {
        const refl = r.call(LensReflector);
        const inst = r.call(Activator).call(providerType);
        refl.extend(inst);
    };
